import React from 'react';

import { Box, BoxProps, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import dayjs from '@helpers/dateUtils';
import { useBreakpointV2 } from '@helpers/use';
import { AcademicCourseEnrollmentStatus } from '@redux/course/type/courseDetailType';

type PurchaseRemarkProps = {
  enrollmentStatus: AcademicCourseEnrollmentStatus;
  examExpiredAt?: Date;
  sx?: BoxProps['sx'];
  isFooter?: boolean;
};

const PurchaseRemark = ({ sx = {}, enrollmentStatus, examExpiredAt, isFooter }: PurchaseRemarkProps) => {
  const theme = useTheme();
  const { t } = useTranslation('store_course');
  const { isMobile } = useBreakpointV2();

  const icon = {
    info: 'fa-light fa-circle-exclamation',
    passed: 'fa-light fa-circle-check',
    failed: 'fa-light fa-circle-xmark',
  };

  const remark = {
    [AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLED]: {
      icon: icon.info,
      message: t('course_detail.header_banner.purchase.remark.academic_audit_enrolled.text', {
        expiredDate: examExpiredAt ? dayjs(examExpiredAt).format('DD/MM/BBBB') : 'XX/XX/XXXX',
      }),
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLMENT_EXPIRED]: { icon: '', message: '' },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLED]: {
      icon: icon.info,
      message: t('course_detail.header_banner.purchase.remark.academic_credit_enrolled.text', {
        expiredDate: examExpiredAt ? dayjs(examExpiredAt).format('DD/MM/BBBB') : 'XX/XX/XXXX',
      }),
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLMENT_EXPIRED]: {
      icon: icon.failed,
      message: t('course_detail.header_banner.purchase.remark.academic_credit_enrollment_expired.text'),
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED]: {
      icon: icon.passed,
      message: t('course_detail.header_banner.purchase.remark.academic_credit_passed'),
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED_REGRADE]: {
      icon: icon.passed,
      message: t('course_detail.header_banner.purchase.remark.academic_credit_passed_regrade'),
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PENDING]: {
      icon: icon.info,
      message: t('course_detail.header_banner.purchase.remark.academic_credit_pending.text'),
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_REGRADE]: {
      icon: icon.failed,
      message: t('course_detail.header_banner.purchase.remark.academic_credit_regrade.text'),
    },
    [AcademicCourseEnrollmentStatus.AVAILABLE_REGISTER]: {
      icon: '',
      message: '',
    },
    [AcademicCourseEnrollmentStatus.PUBLISHED]: {
      icon: icon.failed,
      message: t('course_detail.header_banner.purchase.remark.published.text'),
    },
    [AcademicCourseEnrollmentStatus.UNPUBLISHED]: {
      icon: icon.failed,
      message: t('course_detail.header_banner.purchase.remark.unpublished.text'),
    },
  };

  return (
    <Box sx={{ ...sx }}>
      <Box
        sx={{
          display: 'flex',
          padding: { md: '8px 16px', xs: '8px 8px' },
          minHeight: '46px',
          gap: {md:'16px', xs: '8px'},
          borderRadius: '4px',
          background: theme.palette.stateWhiteOverlay.disabled,
          alignItems: 'center',
        }}
      >
        <Box sx={{ fontSize: { md: '24px', xs: isFooter ? '16px' : '24px' }, padding: { md: '3px', xs: '7px' } }}>
          <FontAwesomeIcon icon={remark[enrollmentStatus]?.icon} />
        </Box>
        <Typography variant={isMobile || isFooter ? 'h6' : 'h4'}>{remark[enrollmentStatus]?.message}</Typography>
      </Box>
    </Box>
  );
};

export default PurchaseRemark;
